import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMessages, useOpenInSunat, useTriggerMessages } from '../../api/messages.js';
import { SUNAT_EXTENSION_URL } from '../../utils/sunatExtension.js';
import { decodeHtmlEntities, formatDateTime } from '../../utils/format.js';
import { messageTypeInfo } from '../../utils/constants.js';
import Spinner from '../Spinner.jsx';
import ErrorBanner from '../ErrorBanner.jsx';

const PAGE_SIZE = 10;

// Lists the messages of one company, or of all companies when no companyId is given.
export default function MessagesPanel({ companyId, companyName }) {
    const { t, i18n } = useTranslation();
    const [page, setPage] = useState(0);

    const { data: messagesPage, isLoading, error } = useMessages(companyId, page, PAGE_SIZE);
    const trigger = useTriggerMessages();
    const openInSunat = useOpenInSunat();
    const openError = openInSunat.error;

    function openMessage(messageId) {
        if (!openInSunat.isPending) openInSunat.mutate(messageId);
    }

    const messages = messagesPage?.content ?? [];
    const pageInfo = messagesPage?.page;

    const subtitle = pageInfo
        ? companyId
            ? t('messages.forCompany', { name: companyName ?? companyId, count: pageInfo.totalElements })
            : t('messages.subtitle', { count: pageInfo.totalElements })
        : null;

    return (
        <div className="workspace-panel messages-panel">
            <div className="panel-header">
                <div className="panel-header-titles">
                    <h2>{t('messages.title')}</h2>
                    {subtitle && <div className="panel-subtitle">{subtitle}</div>}
                </div>
                <div className="panel-header-actions">
                    {companyId && (
                        <Link to={`/?companyId=${encodeURIComponent(companyId)}`} className="btn btn-sm">
                            {t('messages.openInCompanies')}
                        </Link>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        title={t('messages.retrieveHint')}
                        disabled={trigger.isPending}
                        onClick={() => trigger.mutate()}
                    >
                        {trigger.isPending ? t('messages.retrieving') : t('messages.retrieve')}
                    </button>
                </div>
            </div>

            <div className="panel-body">
                <ErrorBanner error={trigger.error} fallback={t('messages.retrieveError')} />
                {trigger.isSuccess && <div className="success-banner">{t('messages.retrieveStarted')}</div>}
                <ErrorBanner error={error} fallback={t('messages.loadError')} />
                {openInSunat.isPending && <div className="success-banner">{t('messages.openingInSunat')}</div>}
                {openError?.code === 'extensionMissing' ? (
                    <div className="error-banner">
                        {t('messages.extensionMissing')}{' '}
                        {SUNAT_EXTENSION_URL && (
                            <a href={SUNAT_EXTENSION_URL} target="_blank" rel="noreferrer">
                                {t('messages.installExtension')}
                            </a>
                        )}
                    </div>
                ) : openError?.code ? (
                    <div className="error-banner">{t('messages.openInSunatError')}</div>
                ) : (
                    <ErrorBanner error={openError} fallback={t('messages.openInSunatError')} />
                )}

                {isLoading && <Spinner label={t('messages.loading')} />}

                {!isLoading && !error && messages.length === 0 && (
                    <div className="panel-empty">{t('messages.empty')}</div>
                )}

                {messages.length > 0 && (
                    <>
                        <div className="table-scroll">
                            <table>
                                <thead>
                                    <tr>
                                        <th>{t('messages.columns.date')}</th>
                                        {!companyId && <th>{t('messages.columns.company')}</th>}
                                        <th>{t('messages.columns.type')}</th>
                                        <th>{t('messages.columns.subject')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {messages.map((message) => {
                                        const { label, tone } = messageTypeInfo(message.messageType, t);
                                        return (
                                            <tr
                                                key={message.id}
                                                className="clickable"
                                                tabIndex={0}
                                                title={t('messages.openInSunat')}
                                                onClick={() => openMessage(message.id)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') openMessage(message.id);
                                                }}
                                            >
                                                <td>{formatDateTime(message.publishDate, i18n.language)}</td>
                                                {!companyId && (
                                                    <td>
                                                        <Link
                                                            to={`/messages/${encodeURIComponent(message.company.id)}`}
                                                            title={t('messages.filterByCompany')}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {message.company.name}
                                                        </Link>
                                                    </td>
                                                )}
                                                <td>
                                                    <span className={`badge badge-${tone}`}>{label}</span>
                                                </td>
                                                <td className="wrap">{decodeHtmlEntities(message.subject)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="pagination">
                            <button
                                type="button"
                                className="btn btn-sm"
                                disabled={page === 0}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                {t('common.previous')}
                            </button>
                            <span>
                                {t('common.pageOf', { page: page + 1, total: Math.max(pageInfo?.totalPages ?? 1, 1) })}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm"
                                disabled={pageInfo ? page + 1 >= pageInfo.totalPages : true}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                {t('common.next')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}